package com.gloopgo.game.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.gloopgo.game.engine.GameConstants
import com.gloopgo.game.network.MultiplayerRepository
import com.gloopgo.game.network.NicknameValidator
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch

/**
 * ViewModel managing the nickname and main menu UI state.
 */
class MenuViewModel(
    private val repository: MultiplayerRepository? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(MenuUiState())
    val uiState: StateFlow<MenuUiState> = _uiState.asStateFlow()

    private var profileJob: Job? = null

    init {
        loadUserProfile()
    }

    fun loadUserProfile() {
        profileJob?.cancel()
        profileJob = viewModelScope.launch {
            val uid = repository?.getCurrentUserId()
            if (uid.isNullOrEmpty() || uid == "current") {
                return@launch
            }
            repository.observeUserProfile(uid)
                .catch { /* ignore stream errors */ }
                .onEach { profile ->
                    profile?.let { p ->
                        val resolvedName = p.name.ifEmpty { _uiState.value.playerName }
                            .take(GameConstants.MAX_PLAYER_NAME_LENGTH)
                        _uiState.value = _uiState.value.copy(
                            playerName = resolvedName,
                            isValid = isNicknameValid(resolvedName),
                            hasProfanity = NicknameValidator.containsBlockedTerm(resolvedName),
                            bestScore = p.bestMass,
                            gamesPlayed = p.gamesPlayed,
                            totalKills = p.totalKills,
                            spBestScore = p.spBestMass,
                            spGamesPlayed = p.spGamesPlayed,
                            spTotalKills = p.spTotalKills
                        )
                    }
                }
                .launchIn(this)
        }
    }

    fun onPlayerNameChange(name: String) {
        val limitedName = if (name.length > GameConstants.MAX_PLAYER_NAME_LENGTH) {
            name.take(GameConstants.MAX_PLAYER_NAME_LENGTH)
        } else {
            name
        }
        _uiState.value = _uiState.value.copy(
            playerName = limitedName,
            isValid = isNicknameValid(limitedName),
            hasProfanity = NicknameValidator.containsBlockedTerm(limitedName)
        )
    }

    fun onSelectEmoji(emoji: String, color: Long?) {
        _uiState.value = _uiState.value.copy(
            selectedEmoji = emoji,
            selectedColor = color
        )
    }

    /**
     * Clears in-memory nickname, skin, and personal stats after remote erase.
     */
    fun resetUserData() {
        profileJob?.cancel()
        profileJob = null
        _uiState.value = MenuUiState()
    }

    fun generateRandomName() {
        val sampleNames = listOf(
            "GloopMaster", "CosmicBlob", "SpeedyGloop", "MegaSlime", "NeonHunter",
            "ChompChamp", "StarGloop", "JellyKing", "TurboBlob", "AquaSphere",
            "ShadowCell", "GoldenGloop", "Blobzilla", "PhantomBlob", "GigaGloop",
            "HyperGloop", "VortexBlob", "PixelGloop", "MightyBlob", "SolarGloop"
        )
        val randomName = sampleNames.random()
        onPlayerNameChange(randomName)
    }

    private fun isNicknameValid(name: String): Boolean {
        val trimmed = name.trim()
        val lengthOk = trimmed.length in GameConstants.MIN_PLAYER_NAME_LENGTH..GameConstants.MAX_PLAYER_NAME_LENGTH
        return lengthOk && !NicknameValidator.containsBlockedTerm(trimmed)
    }
}
